import { nextTick } from 'vue';
/**
 * Composable for adding IDs to DOM elements programmatically
 * Useful for adding test IDs or accessibility IDs to framework components
 */
export function useElementIds() {
    /**
     * Adds IDs to pagination buttons
     * @param {string} containerId - ID of the pagination container
     */
    const addPaginationButtonIds = (containerId = 'data-grid-pagination-component') => {
        nextTick(() => {
            const paginationContainer = document.getElementById(containerId);
            if (paginationContainer) {
                // Add ID to prev button
                const prevButton = paginationContainer.querySelector('.v-pagination__prev button');
                if (prevButton) {
                    prevButton.id = 'pagination-prev-button';
                }
                // Add ID to next button
                const nextButton = paginationContainer.querySelector('.v-pagination__next button');
                if (nextButton) {
                    nextButton.id = 'pagination-next-button';
                }
                // Add IDs to page buttons
                const pageButtons = paginationContainer.querySelectorAll('.v-pagination__item button');
                pageButtons.forEach((button, index) => {
                    const buttonContent = button.querySelector('.v-btn__content');
                    if (buttonContent) {
                        const pageNumber = buttonContent.textContent?.trim();
                        if (pageNumber === '...') {
                            button.id = `pagination-ellipsis-button-${index}`;
                        }
                        else {
                            button.id = `pagination-page-button-${pageNumber?.replace('.', '')}`;
                        }
                    }
                });
            }
        });
    };
    /**
     * Adds ID to a dropdown element's clickable area
     * @param {string} baseId - Base ID for the dropdown
     * @param {string} wrapperSelector - CSS selector for the wrapper element
     */
    const addDropdownSelectId = (baseId, wrapperSelector = '.en-select-wrapper') => {
        if (baseId) {
            nextTick(() => {
                const wrapper = document.querySelector(`${wrapperSelector}:has(#${baseId})`);
                if (wrapper) {
                    const vInput = wrapper.querySelector('.v-input');
                    if (vInput && !vInput.id) {
                        vInput.id = `${baseId}-select`;
                    }
                }
            });
        }
    };
    /**
     * Adds IDs to dropdown menu options when they appear
     * @param {string} baseId - Base ID for the dropdown
     * @param {string} menuSelector - CSS selector for the dropdown menu container
     */
    const addDropdownOptionIds = (baseId, menuSelector = '.en-dropdown-menu--en-select') => {
        if (baseId) {
            const observer = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    if (mutation.addedNodes.length > 0) {
                        const menuContainer = document.querySelector(menuSelector);
                        if (menuContainer) {
                            const menuOptions = menuContainer.querySelectorAll('.v-list-item');
                            menuOptions.forEach((option, index) => {
                                if (!option.id) {
                                    const optionTitle = option.querySelector('.v-list-item-title');
                                    if (optionTitle) {
                                        const optionText = optionTitle.textContent?.trim();
                                        option.id = `${baseId}-option-${optionText}`;
                                    }
                                    else {
                                        option.id = `${baseId}-option-${index}`;
                                    }
                                }
                            });
                        }
                    }
                });
            });
            observer.observe(document.body, {
                childList: true,
                subtree: true
            });
            return () => observer.disconnect();
        }
    };
    /**
     * Generic function to add ID to any element by selector
     * @param {string} selector - CSS selector for the target element
     * @param {string} id - ID to assign
     * @param {Element} container - Optional container to search within
     */
    const addElementId = (selector, id, container = document) => {
        nextTick(() => {
            const element = container.querySelector(selector);
            if (element && !element.id) {
                element.id = id;
            }
        });
    };
    return {
        addPaginationButtonIds,
        addDropdownSelectId,
        addDropdownOptionIds,
        addElementId
    };
}
